// Scatter primitive: one candidate per lattice cell (disc, rounded-box or cross), band-limited edge.
// Used by dirt (pebbles), mulch (chips), sand/snow (sparkle glints), starfield (stars), etc.
export const SCATTER_GLSL = `
struct ScatterSample { float mask; float dome; float height; float fade; int cellID; };

// One candidate per lattice cell, jittered.  p in meters; density = cell size (feature
// spacing) in meters; shape: 0 = disc, 1 = rounded box, 2 = cross/diffraction-spike;
// aspect = y/x stretch (for shape 2: arm SLENDERNESS — higher = thinner spikes); rotJitter
// 0..1 scales the per-cell ROTATION only (Set3 §4.4 — decoupled from placement, which is
// placeJitter's job); placeJitter 0..1 scales the per-cell PLACEMENT jitter (a full-rotation
// chip layer must not also get full-swing placement, which snaps the lattice back into rows);
// presence 0..1 = the fraction of cells that spawn a feature (Set3 §4.1 — the lattice-row
// breaker: cells hashing above presence return an empty sample, destroying the visible rows
// and the multi-layer Moire); radiusMin/radiusMax = the per-cell radius as a fraction of the
// cell (Set3 §3.1 — dirt pebbles 0.35..0.9); heightScale converts mask to meters of relief;
// colorJitterSeed offsets the per-cell color hash so stacked scatter layers decorrelate.
// AA (plan §7 + Set3 §4.2): fade = per-scale band-limit — 1 when the cell is >= ~2px,
// fading to 0 when the cell falls below ~1px (the layer's colour-mean mix + the mask/height
// both multiply by it, so sub-pixel scatter converges to a flat average instead of sizzling).
//
// THE TWO COVERAGE CHANNELS (2026-08-13 — the emissive-scatter fix):
//   s.mask = the CRISP footprint (edge width = one pixel by construction).
//   s.dome = the SOFT profile the height is built from (flat-topped, falling to 0 at the rim),
//            WITHOUT the heightScale factor — so a height-less (emissive) layer can still gate
//            its colour by the feature's soft profile.  s.height is exactly s.dome * heightScale.
// Both already carry s.fade.  A layer body that gates colour must use ONE of these: the layer
// body sees the whole CELL otherwise (the colour is returned for every fragment of the cell,
// gated only by s.fade), which paints solid tiles rather than features.  Existing recipes get
// away with ungated colour because the heightBlend blend uses s.height as the colour weight.
ScatterSample scatterSample(vec2 p, float density, int shape, float aspect, float rotJitter, float placeJitter, float presence, float radiusMin, float radiusMax, float heightScale, int colorJitterSeed) {
  float cell = max(density, 1e-5);
  vec2 cellPos = floor(p / cell);
  // NEGATIVE-COORDINATE CAST (the row-lock bug, fixed 2026-08-08): this MUST go through ivec2.
  // A direct float->uint conversion of a NEGATIVE value is UNDEFINED in GLSL ES 300 (spec 5.4.1:
  // out-of-range conversions are undefined) and every driver we see CLAMPS it to 0.  World-XZ is
  // signed, so for any chunk at x < 0 every cell in a row collapsed to u.x == 0 -- and since
  // hash01 mixes as v.x XOR (v.y * K), the cell hash then depended on y ALONE.  Result: the
  // presence gate, the radius hash, the placement jitter and the colour hash were all CONSTANT
  // along x, i.e. fully-populated-or-empty horizontal ROWS of identically-sized, identically
  // coloured pebbles -- exactly the reported symptom, and completely immune to presence/radius
  // tuning (which is why the previous radius-range "row-band fix" changed nothing).
  // float -> ivec2 truncation is well-defined for negatives, and the ivec2 -> uvec2 bitcast is
  // well-defined two's-complement, so the hash sees a distinct value for every signed cell.
  uvec2 u = uvec2(ivec2(cellPos));
  ScatterSample s;
  s.cellID = int(hash01(u) * 1000000.0) + colorJitterSeed;
  float px = matPixelSize();
  // Sub-pixel band-limit fade (see header).  Spec-clean edges — the plan §7's smoothstep(2px,
  // 1px, w) inverts them, which GLSL ES 300 leaves undefined.
  s.fade = smoothstep(1.0 * px, 2.0 * px, cell);
  // Presence gate (Set3 §4.1): a fraction of cells spawn nothing (zero mask/height, fade
  // untouched — the colour-mean mix still converges via s.fade).  Breaks the lattice rows for
  // every scatter user in one place.  The +311u hash offset decorrelates the gate from the
  // placement/radius/colour hashes below.
  if (hash01(u + 311u) > presence) {
    s.mask = 0.0;
    s.dome = 0.0;
    s.height = 0.0;
    return s;
  }
  vec2 j = vec2(hash01(uvec2(u.x + 91u, u.y + 47u)), hash01(uvec2(u.x + 37u, u.y + 83u))) - 0.5;
  vec2 center = (cellPos + 0.5 + j * placeJitter) * cell;
  float radius = cell * mix(max(radiusMin, 1e-4), max(radiusMax, 1e-4), hash01(uvec2(u.x + 13u, u.y + 29u)));
  vec2 d = (p - center) / radius;
  if (shape == 1) {
    d.y /= max(aspect, 0.05);
    float ang = hash01(uvec2(u.x + 61u, u.y + 97u)) * 6.28318530718 * rotJitter;
    vec2 r = vec2(cos(ang), sin(ang));
    d = mat2(r.x, r.y, -r.y, r.x) * d;
    float q = length(max(abs(d) - vec2(0.7), vec2(0.0))) - 0.3;
    float soft = clamp(px / radius, 1e-4, 0.4);
    s.mask = (1.0 - smoothstep(-soft, soft, q)) * s.fade;
    // Height uses a DOME, never the mask (see the disc branch below for why).
    s.dome = (1.0 - smoothstep(-0.35, 0.35, q)) * s.fade;
    s.height = (1.0 - smoothstep(-0.35, 0.35, q)) * s.fade * heightScale;
    return s;
  }
  if (shape == 2) {
    // CROSS / diffraction spike (2026-08-13): the 4-point photographic star.  Two crossed
    // elongated boxes, unioned by min() of their SDFs — the classic cross SDF.  aspect is
    // the arm SLENDERNESS (arm half-width = 1/aspect in radius units), so a single knob takes
    // it from a fat plus-sign (aspect ~2) to a hairline 4-point star (aspect ~12).  rotJitter
    // rotates the whole cross per cell (0 = every star's spikes axis-aligned, which is the
    // PHOTOGRAPHIC read: real diffraction spikes come from the aperture blades, so they are
    // identically oriented across the whole image — keep rotJitter 0 for that).
    float ang = hash01(uvec2(u.x + 61u, u.y + 97u)) * 6.28318530718 * rotJitter;
    vec2 r = vec2(cos(ang), sin(ang));
    d = mat2(r.x, r.y, -r.y, r.x) * d;
    float w = 1.0 / max(aspect, 1.0);   // arm half-width in radius units
    vec2 e = abs(d);
    // Two axis-aligned boxes: (length 1, half-width w) along x, and its transpose along y.
    float qx = max(e.x - 1.0, e.y - w);
    float qy = max(e.y - 1.0, e.x - w);
    float q = min(qx, qy);
    float soft = clamp(px / radius, 1e-4, 0.4);
    s.mask = (1.0 - smoothstep(-soft, soft, q)) * s.fade;
    // A cross carries no plausible RELIEF (it is a lens artifact, not a solid), so the dome is
    // the same soft profile as the mask, just wider — used for colour gating, and the height
    // is normally 0 for a spike layer anyway.
    s.dome = (1.0 - smoothstep(-0.3, 0.1, q)) * s.fade;
    s.height = (1.0 - smoothstep(-0.3, 0.1, q)) * s.fade * heightScale;
    return s;
  }
  float dist = length(d);
  float soft = clamp(px / radius, 1e-4, 0.4);
  s.mask = (1.0 - smoothstep(1.0 - soft, 1.0, dist)) * s.fade;
  // Height is a SQUASHED smooth DOME over the whole footprint, NOT the mask.  The mask's edge
  // is pixel-width BY CONSTRUCTION (soft = px/radius), so a mask-derived height field has a
  // gradient of ~heightScale/px — i.e. a rim that stays razor-sharp at every zoom, which the
  // eval-stage finite-difference normal turns into a near-horizontal normal ring and the pow-32
  // specular lobe turns into hard bright/black haloes.  A dome's gradient is bounded by
  // ~heightScale/radius (a real, zoom-stable surface slope) and reads as a rounded pebble.
  // Set3 §3.1.3 — the rim SQUASH: the dome is flat-topped until dist 0.55 and falls to 0 at
  // the rim, so the rim gradient is near-zero everywhere (a full dome's rim stays
  // ~heightScale/px steep at sub-pixel sizes, which is the "stretchy/glitchy" zoomed-out read
  // of dirt's pebble rims).  Near-zero at the rim kills the razor edge, the dark ring, and the
  // specular halo at every zoom.
  s.dome = (1.0 - smoothstep(0.55, 1.0, dist)) * s.fade;
  s.height = (1.0 - smoothstep(0.55, 1.0, dist)) * s.fade * heightScale;
  return s;
}
`;
